import type { ChangeEvent } from 'react';
import type { BackupArchivedIntegration, BackupOverview } from '../types';
import { Icon } from './Icon';
import { Modal } from './Modal';
import { getBranding } from '../branding';

interface PreparedReactivationTargetKit {
  googleAccountEmail: string;
  repositoryId: string;
  kitIdentity: {
    googleAccountId: string;
  };
}

interface BackupReactivationDialogProps {
  integration: BackupArchivedIntegration | null;
  overview: BackupOverview | null;
  sourceHasRepository: boolean;
  switchProofConfirmed: boolean;
  switchExportOperationId: string;
  switchKitReady: boolean;
  switchKitDownloaded: boolean;
  targetKit: PreparedReactivationTargetKit | null;
  confirmation: string;
  error: string;
  processingRecoveryResult: boolean;
  busyAction: string;
  actionDisabled: boolean;
  needsDisconnect: boolean;
  needsAccountReset: boolean;
  onClose: () => void;
  onExportSwitchKit: () => void;
  onDownloadSwitchKit: () => void;
  onAcknowledgeSwitchKit: () => void;
  onLoadTargetKit: (file: File | undefined) => void;
  onRemoveTargetKit: () => void;
  onConfirmationChange: (value: string) => void;
  onReactivate: () => void;
  onManageConnection: () => void;
}

function formatTimestamp(value?: string): string {
  if (!value) return 'Not yet';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return 'Unknown';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function BackupReactivationDialog({
  integration,
  overview,
  sourceHasRepository,
  switchProofConfirmed,
  switchExportOperationId,
  switchKitReady,
  switchKitDownloaded,
  targetKit,
  confirmation,
  error,
  processingRecoveryResult,
  busyAction,
  actionDisabled,
  needsDisconnect,
  needsAccountReset,
  onClose,
  onExportSwitchKit,
  onDownloadSwitchKit,
  onAcknowledgeSwitchKit,
  onLoadTargetKit,
  onRemoveTargetKit,
  onConfirmationChange,
  onReactivate,
  onManageConnection,
}: BackupReactivationDialogProps) {
  const branding = getBranding();
  const connected = overview?.connectionStatus === 'connected' && Boolean(overview.config.google.connected);
  const attentionRequired = overview?.connectionStatus === 'attention_required';
  const targetKitMatchesSummary = Boolean(
    integration && targetKit && targetKit.repositoryId.startsWith(integration.repositoryIdPrefix),
  );
  const sameAccount = Boolean(
    connected && targetKit && overview?.config.google.accountId === targetKit.kitIdentity.googleAccountId,
  );
  const readyToAuthenticate = Boolean(
    integration?.reactivationAvailable && overview?.lifecycleActions.reactivate_repository_generation.available &&
    overview?.controller.publicKey && switchProofConfirmed && targetKitMatchesSummary &&
    confirmation === 'REACTIVATE' && !needsDisconnect && !needsAccountReset && !attentionRequired && !actionDisabled,
  );

  return <Modal
    open={Boolean(integration)}
    title="Reactivate archived integration"
    description="Reuse this archived generation and its existing encrypted repository without copying or deleting either destination."
    width="large"
    onClose={onClose}
    footer={<button className="button button--secondary" type="button" onClick={onClose}>Close</button>}
  >
    {integration && <div className="backup-reactivation">
      {error && <p className="form-alert" role="alert">{error}</p>}
      <div className="backup-reactivation__notice">
        <Icon name="warning" size={18} />
        <div>
          <strong>This switches which preserved history is current.</strong>
          <span>The source repository remains in Google Drive and becomes archived. The target repository is reused, not initialized or copied. Cancelling or failing before commit leaves the current integration unchanged.</span>
        </div>
      </div>

      <section className="backup-reactivation__step">
        <span>1</span>
        <div>
          <h3>Review current and archived destinations</h3>
          <div className="backup-reactivation__comparison">
            <article>
              <strong>Current integration</strong>
              <dl>
                <div><dt>Account</dt><dd>{overview?.config.google.account ?? 'No connected account'}</dd></div>
                <div><dt>Folder</dt><dd>{overview?.config.google.folderName ?? 'No current Drive folder'}</dd></div>
                <div><dt>Generation</dt><dd>{overview?.currentGeneration?.number ?? '—'}</dd></div>
                <div><dt>Repository</dt><dd><code>{overview?.config.repositoryId?.slice(0, 12) ?? 'No repository'}</code></dd></div>
              </dl>
            </article>
            <article>
              <strong>Archived target</strong>
              <dl>
                <div><dt>Account</dt><dd>{integration.accountEmail}</dd></div>
                <div><dt>Folder</dt><dd>{integration.folderDisplayName}</dd></div>
                <div><dt>Generation</dt><dd>{integration.number ?? '—'}</dd></div>
                <div><dt>Repository</dt><dd><code>{integration.repositoryIdPrefix}</code></dd></div>
                <div><dt>Detached</dt><dd>{formatTimestamp(integration.detachedAt)}</dd></div>
                <div><dt>Last successful backup</dt><dd>{formatTimestamp(integration.lastSuccessfulBackupAt)}</dd></div>
                <div><dt>Last verified backup</dt><dd>{formatTimestamp(integration.lastVerifiedBackupAt)}</dd></div>
              </dl>
            </article>
          </div>
          <p>The archived target’s snapshots, restore tests, and operation history become current again. Newer snapshots in the current source are not copied into the target. After success, {branding.productName} will require a catch-up backup for current VPS data.</p>
        </div>
      </section>

      <section className="backup-reactivation__step">
        <span>2</span>
        <div>
          <h3>Protect the integration being switched away</h3>
          {!sourceHasRepository ? <p className="backup-reactivation__complete"><Icon name="check" size={16} />No source repository exists, so a switch-away recovery kit is not required.</p> : switchProofConfirmed ? <p className="backup-reactivation__complete"><Icon name="check" size={16} />Server proof confirms this session downloaded the current generation’s integration-switch recovery kit.</p> : <>
            <p>Export a fresh kit with purpose <code>integration_switch</code>. Download and acknowledge this exact operation before continuing.</p>
            {!switchExportOperationId && <button className="button button--primary" type="button" disabled={actionDisabled || processingRecoveryResult || !overview?.controller.publicKey} onClick={onExportSwitchKit}>Confirm password & export switch-away recovery kit</button>}
            {switchExportOperationId && !switchKitReady && <p role="status">Preparing recovery kit for operation <code>{switchExportOperationId}</code>…</p>}
            {switchKitReady && <div className="backup-recovery-download"><strong>Switch-away recovery kit ready</strong><span>Store it outside this server before acknowledging the download.</span><div className="backup-setup-card__actions"><button className="button button--primary" type="button" onClick={onDownloadSwitchKit}>Download switch-away recovery kit</button><button className="button button--secondary" type="button" disabled={!switchKitDownloaded || busyAction === 'acknowledge_recovery_kit'} onClick={onAcknowledgeSwitchKit}>{busyAction === 'acknowledge_recovery_kit' ? 'Confirming…' : 'Acknowledge this operation’s download'}</button></div></div>}
          </>}
        </div>
      </section>

      <section className="backup-reactivation__step">
        <span>3</span>
        <div>
          <h3>Load the archived target’s recovery kit</h3>
          <p>The browser validates the JSON locally. The kit and plaintext repository password are never uploaded; only the password’s controller-wrapped ciphertext is sent after owner authentication.</p>
          {targetKit ? <div className="backup-reactivation__loaded"><Icon name="check" size={17} /><div><strong>Target kit loaded locally</strong><span>{targetKit.googleAccountEmail} · repository <code>{targetKit.repositoryId.slice(0, 12)}</code></span></div><button className="text-button" type="button" onClick={onRemoveTargetKit}>Remove</button></div> : <label className={`button button--secondary${actionDisabled ? ' is-disabled' : ''}`}>Choose archived recovery-kit JSON<input className="sr-only" type="file" accept="application/json,.json" disabled={actionDisabled} onChange={(event: ChangeEvent<HTMLInputElement>) => { onLoadTargetKit(event.target.files?.[0]); event.target.value = ''; }} /></label>}
          <small>Folder display names are not compared because the stable Drive folder may have been renamed.</small>
        </div>
      </section>

      <section className="backup-reactivation__step">
        <span>4</span>
        <div>
          <h3>Confirm the generation switch</h3>
          <p>Automation remains paused after success until schedule and retention are reviewed. Manual <strong>Back up now</strong> remains independently available after credential finalization.</p>
          <label className="backup-field">Type REACTIVATE to continue<input value={confirmation} onChange={(event) => onConfirmationChange(event.target.value)} autoComplete="off" spellCheck={false} disabled={!switchProofConfirmed || !targetKit} /></label>
        </div>
      </section>

      <section className="backup-reactivation__step">
        <span>5</span>
        <div>
          <h3>Confirm the owner password</h3>
          {attentionRequired && <div className="backup-reactivation__blocked" role="status"><strong>Current integration needs attention.</strong><span>Reauthorize or disconnect it before reactivation.</span></div>}
          {needsAccountReset && <div className="backup-reactivation__blocked" role="status"><strong>Reset the uninitialized Google connection first.</strong><span>A connected but uninitialized source must use Choose a different Google account to return to a clean state before reactivation.</span><button className="button button--secondary" type="button" onClick={onManageConnection}>Manage current connection</button></div>}
          {needsDisconnect && <div className="backup-reactivation__blocked" role="status"><strong>Disconnect the current integration first.</strong><span>The loaded target kit belongs to a different stable Google account. Disconnect prevents a mistaken target authorization from invalidating the live source credential.</span><button className="button button--secondary" type="button" onClick={onManageConnection}>Manage current connection</button></div>}
          <button className="button button--primary" type="button" disabled={!readyToAuthenticate} onClick={onReactivate}>Confirm password & reactivate integration</button>
          {!integration.reactivationAvailable && integration.blockedReason && <small role="status">{integration.blockedReason}</small>}
        </div>
      </section>

      <section className="backup-reactivation__step">
        <span>6</span>
        <div>
          <h3>{sameAccount ? 'Same-account verification—no Google popup' : 'Authorize the archived Google account'}</h3>
          <p>{sameAccount
            ? `The connected stable account matches the loaded target kit. ${branding.productName} reuses and verifies that credential without opening OAuth.`
            : `After owner authentication, ${branding.productName} opens Google authorization and keeps the durable operation waiting until consent, repository validation, and candidate cleanup complete. You may leave this page and return.`}</p>
          <small>Cancellation before commit preserves the current integration. Untouched authorization can cancel immediately; a started credential transition remains active until exact candidate/OAuth cleanup is confirmed. Cancellation cannot reverse a generation switch that has already committed.</small>
        </div>
      </section>
    </div>}
  </Modal>;
}
